"use client";

import { useState, useCallback, type FC } from "react";
import { useTranslation } from "react-i18next";
import { ComposerPrimitive, useAui } from "@assistant-ui/react";
import { PlusIcon } from "lucide-react";
import { ComposerDocumentsStrip } from "./ComposerDocumentsStrip";
import { ComposerAction } from "./ComposerAction";
import { ComposerAnonymizationSwitch } from "./ComposerAnonymizationSwitch";
import { ComposerAttachments } from "@/components/assistant-ui/attachment";
import { SourceUnavailableDialog } from "./SourceUnavailableDialog";
import { PASTE_TO_ATTACHMENT_THRESHOLD } from "../constants";

export const Composer: FC = () => {
  const { t } = useTranslation();
  const aui = useAui();
  const [dragging, setDragging] = useState(false);
  const dragCounter = { current: 0 };

  const onDragEnter = useCallback(() => {
    dragCounter.current += 1;
    setDragging(true);
  }, []);
  const onDragLeave = useCallback(() => {
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setDragging(false);
    }
  }, []);
  const onDrop = useCallback(() => {
    dragCounter.current = 0;
    setDragging(false);
  }, []);

  const handlePaste = useCallback(
    async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      // Skip when the clipboard carries files — let assistant-ui's built-in
      // file-paste handler deal with those.
      if (e.clipboardData.files.length > 0) return;

      const text = e.clipboardData.getData("text/plain");
      if (text.length < PASTE_TO_ATTACHMENT_THRESHOLD) return;

      e.preventDefault();
      const file = new File([text], "pasted-text.txt", { type: "text/plain" });
      await aui.composer().addAttachment(file);
    },
    [aui],
  );

  return (
    <ComposerPrimitive.Root className="aui-composer-root relative flex w-full flex-col">
      <ComposerPrimitive.AttachmentDropzone
        className="aui-composer-attachment-dropzone relative flex w-full flex-col rounded-t-2xl border border-input border-b-0 bg-background overflow-hidden outline-none transition-shadow has-[textarea:focus-visible]:border-ring has-[textarea:focus-visible]:ring-2 has-[textarea:focus-visible]:ring-ring/20"
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        {/* Drag overlay */}
        {dragging && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded-t-2xl bg-background/90 backdrop-blur-sm border-2 border-dashed border-ring">
            <PlusIcon className="size-10 text-ring" />
            <span className="text-sm font-medium text-ring">{t("composer.dropFiles")}</span>
          </div>
        )}

        <ComposerAttachments />
        <ComposerDocumentsStrip />
        <ComposerPrimitive.Input
          placeholder={t("thread.composerPlaceholder")}
          className="aui-composer-input mb-1 max-h-32 min-h-16 w-full resize-none bg-transparent px-4 pt-3 pb-3 font-body text-sm outline-none placeholder:text-muted-foreground focus-visible:ring-0"
          rows={1}
          autoFocus
          onPaste={handlePaste}
          aria-label={t("thread.composerLabel")}
        />
        <ComposerAction />
      </ComposerPrimitive.AttachmentDropzone>

      {/*
        Bottom dock — sits flush against the composer frame as a continuation
        (rounded-b-2xl, shared border, no top border). Slightly greyish
        background blends the dock into the composer visually while keeping
        controls outside the typing area. Left side reserved for future
        toggles; the anonymization switch lives right-aligned today.
      */}
      <div className="aui-composer-dock flex items-center justify-between rounded-b-2xl border border-t-0 border-input bg-muted/40 px-3 py-3">
        <div className="aui-composer-dock-left flex items-center gap-2" />
        <div className="aui-composer-dock-right flex items-center gap-2">
          <ComposerAnonymizationSwitch />
        </div>
      </div>
      <SourceUnavailableDialog />
    </ComposerPrimitive.Root>
  );
};
